import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Toaster } from 'react-hot-toast';
import { FormationsProvider } from './contexts/FormationsContext';
import { AuthProvider } from './contexts/AuthContext';
import { TeamProvider } from './contexts/TeamContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { TeamPlayerView } from './components/TeamPlayerView';
import { MobileNav } from './components/layout/MobileNav';
import { OfflineBanner } from './components/OfflineBanner';
import { useAndroidTerugknop } from './hooks/useAndroidTerugknop';
import { DeepLinks } from './components/DeepLinks';

// Eager load: Authentication routes (small, needed immediately)
import EmailVerification from './pages/EmailVerification';
import ResetPassword from './pages/ResetPassword';
import AcceptInvitation from './pages/AcceptInvitation';
import NotFound from './pages/NotFound';

// Geeft Sentry de route-patronen in plaats van de ingevulde URL, zodat
// /team/abc-def/mixed-1 en /team/ghi-jkl/dames-3 als één transactie tellen.
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

// Lazy load: All coach routes (only loaded when user navigates to /coach)
const CoachDashboard = lazy(() => import('./pages/CoachDashboard'));
const Wedstrijden = lazy(() => import('./pages/coach/Wedstrijden'));
const WedstrijdDetail = lazy(() => import('./pages/coach/WedstrijdDetail'));
const Spelers = lazy(() => import('./pages/coach/Spelers'));
const SpelerDetail = lazy(() => import('./pages/coach/SpelerDetail'));
const SpelerVergelijken = lazy(() => import('./pages/coach/SpelerVergelijken'));
const Formaties = lazy(() => import('./pages/coach/Formaties'));
const Oefeningen = lazy(() => import('./pages/coach/Oefeningen'));
const OefeningDetail = lazy(() => import('./pages/coach/OefeningDetail'));
const OefeningBewerken = lazy(() => import('./pages/coach/OefeningBewerken'));
const FormationDetail = lazy(() => import('./pages/FormationDetail'));
const Statistieken = lazy(() => import('./pages/coach/Statistieken'));
const Analyse = lazy(() => import('./pages/coach/Analyse'));
const LiveVerslag = lazy(() => import('./pages/coach/LiveVerslag'));
const AnalyseVergelijken = lazy(() => import('./pages/coach/AnalyseVergelijken'));
const Instellingen = lazy(() => import('./pages/coach/Instellingen'));
const TeamBeheerDetail = lazy(() => import('./pages/coach/TeamBeheerDetail'));

/**
 * Meldt de Android-terugknop aan. Een eigen component omdat de hook useNavigate gebruikt
 * en dus binnen de <Router> moet leven, terwijl App die Router zelf rendert.
 */
const AndroidTerugknop = () => {
  useAndroidTerugknop();
  return null;
};

// Loading fallback component for Suspense
const LoadingFallback = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hockey-blue mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <TeamProvider>
        <NotificationProvider>
          <FormationsProvider>
            <Router>
            <AndroidTerugknop />
            <div className="App min-h-screen bg-gray-50">
              {/* De enige Toaster in de app. react-hot-toast houdt één globale wachtrij
                  en elke gemonteerde Toaster rendert daar álles uit, dus een tweede op
                  paginaniveau tekent iedere melding een keer extra - exact op de eerste,
                  waardoor je het niet ziet maar wel hebt.

                  De offset houdt de melding onder de statusbalk. In de native app loopt
                  de webview tot de bovenrand van het scherm, en zonder dit verdwijnt een
                  toast achter de Dynamic Island. Op web is --safe-top nul en verandert er
                  niets. */}
              <Toaster
                position="top-center"
                containerStyle={{ top: 'calc(var(--safe-top) + 12px)' }}
                toastOptions={{
                  // react-hot-toast tekent standaard wit met donkere tekst, als inline
                  // stijl - dus zonder dit blijft er in donkere modus een wit blokje boven
                  // de pagina hangen. var() en geen uitgelezen waarde: de toast leeft in
                  // een portal onder <body> en erft de variabelen van :root, dus hij
                  // verkleurt mee zonder opnieuw te monteren.
                  style: {
                    background: 'rgb(var(--vlak-kaart))',
                    color: 'rgb(var(--inkt-9))',
                    border: '1px solid rgb(var(--vlak-3))',
                  },
                }}
              />

              {/* Buiten de Routes, zodat de balk niet opnieuw monteert bij elke
                  navigatie en de actieve pil kan meeschuiven. Bewust vóór de Routes:
                  paginamodals staan ook op z-50 en moeten later in de DOM komen om
                  de balk af te dekken. MobileNav bepaalt zelf of hij zichtbaar is. */}
              <MobileNav />

              {/* Vangt links naar clubcoachapp.nl op die iemand buiten de app aantikt en
                  zet ze om in een route. Moet binnen de Router staan. */}
              <DeepLinks />

              {/* Bepaalt zelf of hij zichtbaar is; staat hier zodat elk scherm hem
                  krijgt, ook de publieke opstellingspagina. */}
              <OfflineBanner />

              <Suspense fallback={<LoadingFallback />}>
                <SentryRoutes>
                  {/* Homepage - static HTML serves at / */}

                  {/* Authentication routes - no protection needed */}
                  <Route path="/verify-email" element={<EmailVerification />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/accept-invitation" element={<AcceptInvitation />} />

                  {/* Team-specific player routes */}
                  {/* De volgpagina van het live verslag: /team/abcd-efgh/team-name/live. Een vast
                      segment gaat in React Router altijd vóór :matchId hieronder. */}
                  <Route path="/team/:publicId/:slug/live" element={<TeamPlayerView live />} />
                  {/* Het verslag van één wedstrijd, zoals de badge op de opstelling ernaar linkt. */}
                  <Route path="/team/:publicId/:slug/:matchId/live" element={<TeamPlayerView live />} />
                  {/* New secure format with specific match: /team/abcd-efgh/team-name/matchId */}
                  <Route path="/team/:publicId/:slug/:matchId" element={<TeamPlayerView />} />
                  {/* New secure format: /team/abcd-efgh/team-name */}
                  <Route path="/team/:publicId/:slug" element={<TeamPlayerView />} />
                  {/* Legacy format for backwards compatibility (will be deprecated) */}
                  <Route path="/team/:slug" element={<TeamPlayerView />} />

                  {/* Coach routes - contextual with team switcher.
                      LET OP: nginx.conf.template somt de toegestane eerste segmenten
                      hieronder op, zodat onbekende paden een echte 404 krijgen in plaats
                      van index.html met status 200. Een nieuwe route hier vraagt dus ook
                      om een regel daar. */}
                  <Route path="/coach" element={<ProtectedRoute><CoachDashboard /></ProtectedRoute>} />
                  <Route path="/coach/wedstrijden" element={<ProtectedRoute><Wedstrijden /></ProtectedRoute>} />
                  <Route path="/coach/wedstrijden/:id" element={<ProtectedRoute><WedstrijdDetail /></ProtectedRoute>} />
                  {/* Twee segmenten diep: nginx.conf.template heeft hiervoor een eigen blok. */}
                  <Route path="/coach/wedstrijden/:id/live" element={<ProtectedRoute><LiveVerslag /></ProtectedRoute>} />
                  <Route path="/coach/spelers" element={<ProtectedRoute><Spelers /></ProtectedRoute>} />
                  <Route path="/coach/spelers/vergelijken" element={<ProtectedRoute><SpelerVergelijken /></ProtectedRoute>} />
                  <Route path="/coach/spelers/:id" element={<ProtectedRoute><SpelerDetail /></ProtectedRoute>} />
                  <Route path="/coach/formaties" element={<ProtectedRoute><Formaties /></ProtectedRoute>} />
                  <Route path="/coach/formaties/:id" element={<ProtectedRoute><FormationDetail /></ProtectedRoute>} />
                  {/* /nieuw bestaat niet als route: de basisgegevens staan in een venster op
                      de lijst. Komt hij er ooit bij, zet hem dan vóór /:id. */}
                  <Route path="/coach/oefeningen" element={<ProtectedRoute><Oefeningen /></ProtectedRoute>} />
                  <Route path="/coach/oefeningen/:id/bewerken" element={<ProtectedRoute><OefeningBewerken /></ProtectedRoute>} />
                  <Route path="/coach/oefeningen/:id" element={<ProtectedRoute><OefeningDetail /></ProtectedRoute>} />
                  <Route path="/coach/analyse" element={<ProtectedRoute><Analyse /></ProtectedRoute>} />
                  <Route path="/coach/analyse/vergelijken" element={<ProtectedRoute><AnalyseVergelijken /></ProtectedRoute>} />
                  <Route path="/coach/statistieken" element={<ProtectedRoute><Statistieken /></ProtectedRoute>} />
                  {/* Tab in het pad, zodat een link naar een specifiek tabblad deelbaar is */}
                  <Route path="/coach/statistieken/:tab" element={<ProtectedRoute><Statistieken /></ProtectedRoute>} />
                  <Route path="/coach/instellingen" element={<ProtectedRoute><Instellingen /></ProtectedRoute>} />
                  {/* Alleen voor admins; de pagina stuurt anderen terug naar Instellingen. */}
                  <Route path="/coach/instellingen/teams/:id" element={<ProtectedRoute><TeamBeheerDetail /></ProtectedRoute>} />

                  {/* Op het web serveert nginx de statische homepage op '/' en mount React
                      daar niet, dus deze route wordt alleen in de native app geraakt. Daar
                      is '/' het startpunt en hoort de gebruiker in de app te belanden;
                      ProtectedRoute toont het inlogscherm als er geen sessie is. */}
                  <Route path="/" element={<Navigate to="/coach" replace />} />

                  {/* Vangnet. Nginx stuurt alles onder /coach en /team naar index.html,
                      dus onbekende paden dáárbinnen komen hier terecht in plaats van bij
                      public/404.html. Zonder deze route bleef het scherm wit. */}
                  <Route path="*" element={<NotFound />} />
                </SentryRoutes>
              </Suspense>
            </div>
            </Router>
          </FormationsProvider>
        </NotificationProvider>
      </TeamProvider>
    </AuthProvider>
  );
}

export default App;
